import { useState } from 'react';
import { Boxes, Loader2, X } from 'lucide-react';
import { apiService, PackGroupDTO, PackOversizedBatchEntry, PackOversizedBatchReport } from '@/services/api.service';
import styles from './Oversized.module.scss';

const VERDICT_LABEL: Record<string, string> = {
  too_big: 'Trop gros',
  limit: 'À vérifier',
  overflow: 'Déborde',
  fits: 'Tient',
};

const ACTION_LABEL: Record<PackOversizedBatchEntry['action'], string> = {
  convert: 'à convertir',
  converted: 'converti',
  already: 'déjà objet',
  skip: 'laissé en bloc',
  error: 'échec',
};

/**
 * Conversion groupée des gros blocs de déco en objets : rapport à blanc d'abord (rien n'est écrit),
 * puis conversion de la sélection. Proposé seulement pour les groupes qui ont le drapeau oversizedBlocks.
 */
export function OversizedBulkConvert({ groups, onDone }: { groups: PackGroupDTO[]; onDone: (groupId: string) => void }) {
  const [open, setOpen] = useState(false);
  const [groupId, setGroupId] = useState(groups[0]?.id ?? '');
  const [report, setReport] = useState<PackOversizedBatchReport | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const gid = groups.some((g) => g.id === groupId) ? groupId : groups[0]?.id ?? '';

  const analyze = async (target = gid) => {
    setBusy(true);
    setError(null);
    const r = await apiService.convertOversizedBlocks(target, { dryRun: true });
    setBusy(false);
    if (!r.data) { setReport(null); setError(r.error || 'Analyse impossible'); return; }
    setReport(r.data);
    if (!r.data.success) setError(r.data.message);
    setChecked(new Set(r.data.entries.filter((e) => e.action === 'convert').map((e) => e.identifier)));
  };

  const convert = async () => {
    if (checked.size === 0) return;
    setBusy(true);
    setError(null);
    const r = await apiService.convertOversizedBlocks(gid, { dryRun: false, identifiers: [...checked] });
    setBusy(false);
    if (!r.data) { setError(r.error || 'Conversion impossible'); return; }
    setReport(r.data);
    setChecked(new Set());
    if (!r.data.success) setError(r.data.message);
    onDone(gid);
  };

  const toggle = (id: string) => setChecked((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });

  const openPanel = () => {
    setOpen(true);
    setReport(null);
    void analyze();
  };

  return (
    <>
      <button className={styles.bulkBtn} onClick={openPanel} title="Convertir les blocs trop gros pour le client en objets PropsiMod">
        <Boxes size={15} /> Convertir les gros blocs
      </button>
      {open && (
        <div className={styles.modalBackdrop} onClick={() => !busy && setOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHead}>
              <strong>Gros blocs de déco : conversion groupée</strong>
              <button className={styles.iconBtn} onClick={() => setOpen(false)} disabled={busy} title="Fermer"><X size={16} /></button>
            </div>

            <div className={styles.modalBar}>
              {groups.length > 1 && (
                <select value={gid} onChange={(e) => { setGroupId(e.target.value); setReport(null); void analyze(e.target.value); }} disabled={busy}>
                  {groups.map((g) => <option key={g.id} value={g.id}>{g.name || g.id}</option>)}
                </select>
              )}
              <button className={styles.secondaryBtn} onClick={() => analyze()} disabled={busy}>
                {busy ? <Loader2 size={14} className={styles.spin} /> : null} Analyser (à blanc)
              </button>
              <button className={styles.primaryBtn} onClick={convert} disabled={busy || checked.size === 0}>
                Convertir la sélection ({checked.size})
              </button>
            </div>

            {error && <div className={`${styles.banner} ${styles.bannerDanger}`}>{error}</div>}
            {report && (
              <>
                <div className={styles.reportMsg}>{report.message}</div>
                <div className={styles.counts}>
                  {Object.entries(report.counts).map(([v, n]) => (
                    <span key={v} className={`${styles.count} ${styles[`v_${v}`] ?? ''}`}>{VERDICT_LABEL[v] ?? v} : {n}</span>
                  ))}
                </div>
                {report.entries.length === 0 ? (
                  <div className={styles.hint}>Aucun bloc trop gros ni à vérifier dans ce groupe.</div>
                ) : (
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr><th /><th>Bloc</th><th>Verdict</th><th>Empreinte</th><th>Action</th><th>Détail</th></tr>
                      </thead>
                      <tbody>
                        {report.entries.map((e) => {
                          const selectable = report.dryRun && !e.object && (e.action === 'convert' || e.action === 'skip');
                          return (
                            <tr key={e.identifier}>
                              <td>
                                <input type="checkbox" disabled={!selectable || busy} checked={checked.has(e.identifier)} onChange={() => toggle(e.identifier)} />
                              </td>
                              <td className={styles.mono}>{e.identifier}</td>
                              <td>
                                {(() => {
                                  // Un bloc trop gros reste « trop gros » même si un seuil est aussi frôlé.
                                  const v = e.strictVerdict === 'too_big' ? 'too_big' : e.verdict;
                                  return <span className={`${styles.count} ${styles[`v_${v}`] ?? ''}`} title={e.summary}>{VERDICT_LABEL[v] ?? v}</span>;
                                })()}
                              </td>
                              <td>{e.footprint === 'display_only' ? 'Affichage seul' : `Cellules (${e.cells.length})`}</td>
                              <td>{ACTION_LABEL[e.action] ?? e.action}</td>
                              <td className={styles.detail} title={e.summary}>{e.message}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className={styles.hint}>
                  Cocher une ligne « laissé en bloc » la force en objet. Rien n'est publié : envoie ensuite le groupe avec le bouton Envoyer.
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
